import { useState, useEffect, useContext } from "react";
import { Select, MenuItem, FormControl, useTheme } from "@mui/material";
import { API, graphqlOperation } from "aws-amplify";
import { updateUser, updatePricelist } from "../../graphql/mutations";
import { listPricelists, getPricelist, getUser, listUsers } from "../../graphql/queries";
import { useUser } from "../../UserContext";
import { AppContext } from "../../App";

const PricelistSelect = ({
  pricelistID = null,
  userID = null,
  onPricelistUpdate = () => {},
  showRecentOnly = false,
  providerID = null,
}) => {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const { userRecord, refreshUserAndPricelist, user } = useUser();
  const { setUserRecord } = useContext(AppContext);
  const theme = useTheme();

  const [showFeedback, setShowFeedback] = useState(false);
  const [userFeedback, setUserFeedback] = useState(null);
  const [pricelists, setPricelists] = useState([]);
  const [allPricelists, setAllPricelists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPricelist, setSelectedPricelist] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [missingPricelist, setMissingPricelist] = useState(null);

  const popFeedback = (message) => {
    setUserFeedback(message);
    setShowFeedback(true);
  };

  const assignPricelist = async (pricelistID) => {
    try {
      console.log(
        `[DEBUG] assignPricelist: Assigning pricelist ${pricelistID} to user ${
          userID || userRecord.id
        }`
      );

      // Get the pricelist details to ensure we have the s3Key
      const pricelistResponse = await API.graphql(
        graphqlOperation(getPricelist, { id: pricelistID })
      );

      const pricelistDetails = pricelistResponse.data.getPricelist;
      console.log("[DEBUG] Pricelist details:", pricelistDetails);

      if (!pricelistDetails) {
        console.error("[DEBUG] Could not find pricelist details");
        popFeedback("Error: Could not find pricelist details");
        return false;
      }

      // Ensure providerId is set on the pricelist to maintain provider-customer relationship
      // If providerId is not set, set it to the pricelist's userId (the owner/provider)
      if (!pricelistDetails.providerId && pricelistDetails.userId) {
        console.log(
          `[DEBUG] Setting providerId to ${pricelistDetails.userId} for pricelist ${pricelistID}`
        );
        try {
          await API.graphql(
            graphqlOperation(updatePricelist, {
              input: {
                id: pricelistID,
                providerId: pricelistDetails.userId,
              },
            })
          );
          console.log("[DEBUG] Pricelist providerId updated successfully");
        } catch (error) {
          console.error("[DEBUG] Error updating pricelist providerId:", error);
          // Continue with user update even if pricelist update fails
        }
      }

      // CRITICAL: When assigning a pricelist, also set the user's createdByUserId to the pricelist owner/provider
      // This ensures the provider dropdown shows the correct provider
      const providerId = pricelistDetails.providerId || pricelistDetails.userId;
      
      const userToUpdate = {
        id: userID ? userID : userRecord.id,
        activePricelistID: pricelistID,
        // Set createdByUserId to the pricelist owner/provider
        ...(providerId && { createdByUserId: providerId }),
      };
      
      console.log(
        "[DEBUG] Updating user with provider relationship:",
        "\n  User ID:", userToUpdate.id,
        "\n  Pricelist ID:", pricelistID,
        "\n  Provider ID (createdByUserId):", providerId,
        "\n  Pricelist userId:", pricelistDetails.userId,
        "\n  Pricelist providerId:", pricelistDetails.providerId
      );

      console.log(
        "[DEBUG] ⚡ ASSIGNING PRICELIST ⚡",
        "\n  User ID being updated:", userToUpdate.id,
        "\n  Pricelist ID:", pricelistID,
        "\n  userID prop:", userID,
        "\n  userRecord.id:", userRecord?.id,
        "\n  Full input:", JSON.stringify(userToUpdate, null, 2)
      );
      const updatedUserResponse = await API.graphql(
        graphqlOperation(updateUser, { input: userToUpdate })
      );

      const updatedUser = updatedUserResponse.data.updateUser;
      console.log(
        "[DEBUG] User update response:",
        JSON.stringify(updatedUser, null, 2)
      );
      
      // Verify the update persisted by fetching the user again
      try {
        const verifyResponse = await API.graphql(
          graphqlOperation(getUser, { id: updatedUser.id })
        );
        const verifiedUser = verifyResponse.data.getUser;
        console.log(
          "[DEBUG] ✓ Verified user after update:",
          "\n  ID:", verifiedUser.id,
          "\n  Email:", verifiedUser.email,
          "\n  activePricelistID:", verifiedUser.activePricelistID,
          "\n  createdByUserId (Provider):", verifiedUser.createdByUserId || "NULL"
        );
        
        if (verifiedUser.activePricelistID !== pricelistID) {
          console.error(
            "[DEBUG] ❌ WARNING: activePricelistID did not persist! Expected:",
            pricelistID,
            "Got:",
            verifiedUser.activePricelistID
          );
        } else {
          console.log("[DEBUG] ✓ Pricelist assignment verified successfully!");
        }
        
        // Verify createdByUserId was set correctly
        if (providerId && verifiedUser.createdByUserId !== providerId) {
          console.warn(
            "[DEBUG] ⚠️ WARNING: createdByUserId did not match expected provider!",
            "\n  Expected:", providerId,
            "\n  Got:", verifiedUser.createdByUserId || "NULL"
          );
        } else if (providerId) {
          console.log("[DEBUG] ✓ Provider relationship (createdByUserId) verified successfully!");
        }
        
        // ALSO: Check if there are other users with the same email
        console.log("[DEBUG] 🔍 Checking for duplicate users with same email...");
        const allUsersResponse = await API.graphql(
          graphqlOperation(listUsers, { limit: 10000 })
        );
        const allUsers = allUsersResponse?.data?.listUsers?.items || [];
        const sameEmailUsers = allUsers.filter(
          (u) => u.email && u.email.toLowerCase().trim() === verifiedUser.email.toLowerCase().trim()
        );
        
        console.log(`[DEBUG] Found ${sameEmailUsers.length} user(s) with email ${verifiedUser.email}:`);
        sameEmailUsers.forEach((u, idx) => {
          const isCurrent = u.id === verifiedUser.id;
          console.log(`  [${idx}]${isCurrent ? ' ⬅️ THIS ONE' : ''} ID: ${u.id}`);
          console.log(`       activePricelistID: ${u.activePricelistID || 'NULL'}`);
          console.log(`       createdByUserId: ${u.createdByUserId || 'NULL'}`);
        });
        
        if (sameEmailUsers.length > 1) {
          console.error("[DEBUG] ⚠️⚠️⚠️ DUPLICATE USERS DETECTED! This will cause login issues! ⚠️⚠️⚠️");
        }
      } catch (verifyError) {
        console.error("[DEBUG] Error verifying user update:", verifyError);
      }

      // Call the callback with the updated user data
      onPricelistUpdate(updatedUser);

      // Only refresh the current user's context if we're updating the current user
      if (userID === userRecord.id || !userID) {
        console.log("[DEBUG] Refreshing current user context");
        await refreshUserAndPricelist(user);
      }

      popFeedback("Pricelist assigned successfully!");
      return true;
    } catch (error) {
      console.error("[DEBUG] Error assigning pricelist:", error);
      popFeedback("Error assigning pricelist");
      return false;
    }
  };

  const handlePricelistSelect = (pricelistID) => {
    setSelectedInvoiceId(pricelistID);
    assignPricelist(pricelistID);
  };

  const fetchPricelists = async () => {
    // If providerID is provided, we're filtering by a specific provider
    // Otherwise, use the default behavior (current user's pricelists)
    if (providerID) {
      // Fetch pricelists for a specific provider
      try {
        setLoading(true);

        // Fetch pricelists owned by the provider (userId field)
        const providerResponse = await API.graphql(
          graphqlOperation(listPricelists, {
            limit: 1000,
            filter: {
              userId: { eq: providerID },
            },
          })
        );

        let pricelistsData = providerResponse.data.listPricelists.items || [];

        // Sort pricelists by createdAt date field (newest first)
        pricelistsData.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        // Limit to 10 most recently created
        pricelistsData = pricelistsData.slice(0, 10);

        // Store all pricelists for reference
        setAllPricelists(pricelistsData);
        setPricelists(pricelistsData);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching provider pricelists:", error);
        setError(error.message);
        setLoading(false);
      }
      return;
    }

    // Default behavior: fetch pricelists for current user
    if (!userRecord || !userRecord.id) {
      console.log("User record not available yet");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch pricelists where user is the owner (userId field)
      const ownedResponse = await API.graphql(
        graphqlOperation(listPricelists, {
          limit: 1000,
          filter: {
            userId: { eq: userRecord.id },
          },
        })
      );

      const ownedPricelists = ownedResponse.data.listPricelists.items || [];

      // Fetch all pricelists and filter client-side for providerId
      // Note: providerId is not available as a filter field in GraphQL schema
      const allResponse = await API.graphql(
        graphqlOperation(listPricelists, {
          limit: 1000,
        })
      );

      const allPricelists = allResponse.data.listPricelists.items || [];

      // Filter for pricelists where user is the provider (client-side filter)
      const providedPricelists = allPricelists.filter(
        (p) => p && p.providerId === userRecord.id
      );

      // Combine and deduplicate by ID
      const combinedPricelists = [...ownedPricelists, ...providedPricelists];
      const uniquePricelistsMap = new Map();

      combinedPricelists.forEach((pricelist) => {
        if (pricelist && pricelist.id) {
          // Keep the first occurrence (prefer owned over provided if duplicate)
          if (!uniquePricelistsMap.has(pricelist.id)) {
            uniquePricelistsMap.set(pricelist.id, pricelist);
          }
        }
      });

      let pricelistsData = Array.from(uniquePricelistsMap.values());

      // If user has an activePricelist that's not in the list (e.g., provided by another user),
      // fetch it separately and add it
      if (
        userRecord.activePricelistID &&
        !pricelistsData.find((p) => p.id === userRecord.activePricelistID)
      ) {
        try {
          const activePricelistResponse = await API.graphql(
            graphqlOperation(getPricelist, { id: userRecord.activePricelistID })
          );
          if (activePricelistResponse.data.getPricelist) {
            pricelistsData.push(activePricelistResponse.data.getPricelist);
          }
        } catch (error) {
          console.log("Error fetching active pricelist:", error);
        }
      }

      // Sort pricelists by createdAt date field (newest first)
      pricelistsData.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      // Store all pricelists for reference
      setAllPricelists(pricelistsData);

      // If showRecentOnly is true, limit to most recent 10 pricelists
      // Otherwise, show all pricelists
      if (showRecentOnly) {
        const recentPricelists = pricelistsData.slice(0, 10);
        setPricelists(recentPricelists);
      } else {
        setPricelists(pricelistsData);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching pricelists:", error);
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricelists();
  }, [userRecord?.id, providerID]);

  // Fetch missing pricelist if selected pricelist is not in the list
  useEffect(() => {
    const fetchMissingPricelist = async () => {
      if (
        pricelistID &&
        !pricelists.find((p) => p.id === pricelistID) &&
        !missingPricelist
      ) {
        try {
          const response = await API.graphql(
            graphqlOperation(getPricelist, { id: pricelistID })
          );
          if (response.data.getPricelist) {
            setMissingPricelist(response.data.getPricelist);
            // Add it to the pricelists list only if it doesn't already exist
            setPricelists((prev) => {
              // Double-check for duplicates before adding
              const exists = prev.some((p) => p && p.id === pricelistID);
              if (!exists && response.data.getPricelist) {
                return [...prev, response.data.getPricelist];
              }
              return prev;
            });
          }
        } catch (error) {
          console.error("Error fetching missing pricelist:", error);
        }
      }
    };

    if (pricelistID && pricelists.length > 0) {
      fetchMissingPricelist();
    }
  }, [pricelistID, pricelists, missingPricelist]);

  return (
    <div style={{ minWidth: "200px", marginBottom: "20px" }}>
      <FormControl fullWidth size="small">
        <Select
          value={pricelistID || ""}
          onChange={(e) => handlePricelistSelect(e.target.value)}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return <em>No pricelist</em>;
            }

            const selectedPricelist = pricelists.find((p) => p.id === selected);
            if (selectedPricelist) {
              // Use name, filename, or fallback to a readable format
              return (
                selectedPricelist.name ||
                selectedPricelist.filename ||
                `Pricelist #${selectedPricelist.id.substring(0, 8)}`
              );
            }

            // Check if we have the missing pricelist loaded
            if (missingPricelist && missingPricelist.id === selected) {
              return (
                missingPricelist.name ||
                missingPricelist.filename ||
                `Pricelist #${selected.substring(0, 8)}`
              );
            }

            // If pricelist not found in list, show a fallback (not the raw ID)
            return <em>Loading pricelist...</em>;
          }}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.secondary.main,
              borderWidth: "2px",
              borderRadius: "30px",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.secondary.main,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.secondary.main,
            },
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            ".MuiSelect-select": {
              padding: "15px 20px",
              fontSize: "18px",
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                maxHeight: showRecentOnly ? "320px" : "400px", // Adjusted height for better visibility
                "& .MuiMenuItem-root": {
                  fontSize: "18px",
                  "&:hover": {
                    backgroundColor: "rgba(64, 243, 72, 0.1)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(64, 243, 72, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(64, 243, 72, 0.3)",
                    },
                  },
                },
              },
            },
          }}
        >
          <MenuItem value="">
            <em>No pricelist</em>
          </MenuItem>
          {pricelists.map((pricelist) => {
            // Display name, filename, or fallback - never show ID or null
            const displayName =
              pricelist.name ||
              pricelist.filename ||
              `Pricelist #${pricelist.id.substring(0, 8)}`;
            return (
              <MenuItem value={pricelist.id} key={pricelist.id}>
                {displayName}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </div>
  );
};

export default PricelistSelect;
